import { Form } from "antd";
import type { Rule } from "antd/es/form";
import { useQuote } from "../QuoteProvider";
import { StyledAntdInput } from "../StyledQuoteInput";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  stringRules,
  emailRules,
  zipRules,
  suggestEmailCorrection,
} from "../../utils/inputValidation";
import { useEmailValidation } from "../../utils/useEmailValidation";
import {
  EMAIL_UNDELIVERABLE_MESSAGE,
  type EmailValidationVerdict,
} from "../../utils/emailValidationCore";
import { isLooseUsPhone } from "../../livestream/liveContactValidation";
import { useTranslation } from "react-i18next";

type QuotePersonalDetailsFormProps = {
  // Lifted to StepContact so the "Get my free evaluation" button can HARD
  // BLOCK on a server-confirmed definitive negative (owner decision,
  // 2026-08-23) rather than only showing a cosmetic Form.Item error.
  onEmailBlockedChange?: (blocked: boolean) => void;
};

/** Form-item names, in on-screen order. Shared with StepContact's hint so it
 *  can scroll/focus the first field the patient still has to fill in. */
export type QuoteContactFormField =
  | "name"
  | "lastname"
  | "email"
  | "phone"
  | "zipCode";

export type QuotePersonalDetailsFormHandle = {
  /** Scroll the named field into view and put the caret in it. Uses the antd
   *  Form instance rather than DOM ids so it can't drift from the rendered
   *  Form.Item names. */
  focusField: (field: QuoteContactFormField) => void;
};

const QuotePersonalDetailsForm = forwardRef<
  QuotePersonalDetailsFormHandle,
  QuotePersonalDetailsFormProps
>(({ onEmailBlockedChange }, ref) => {
  // @ts-ignore - useTranslation with namespace parameter
  const { t } = useTranslation("quote");
  const quoteData = useQuote();
  const [form] = Form.useForm();
  const checkEmail = useEmailValidation();
  const [serverVerdict, setServerVerdict] = useState<EmailValidationVerdict | null>(
    null,
  );
  // Mirrors the live email value outside React state so the async validator
  // below (whose closure is only refreshed when `checkEmail` changes, not on
  // every keystroke) can tell a stale response from a current one. checkEmail
  // itself only cancels/supersedes a PRIOR in-flight check when it's called
  // AGAIN (i.e. on the next blur) — a single blur-then-edit-without-reblur
  // has no second call to trigger that, so the resolution here is the only
  // guard against applying a verdict for a value the user has since changed.
  const latestEmailRef = useRef(quoteData.email);

  useEffect(() => {
    form.setFieldValue("email", quoteData.email);
    form.setFieldValue("zipCode", quoteData.zipCode);
    latestEmailRef.current = quoteData.email;
  }, [quoteData.email]);

  useEffect(() => {
    onEmailBlockedChange?.(serverVerdict?.status === "invalid");
  }, [serverVerdict, onEmailBlockedChange]);

  // Typo suggestion (e.g. "Did you mean amissydee@gmail.com?") — the live
  // server verdict wins when present (it's re-derived from the same
  // canonical map the local check uses, see inputValidation.tsx's parity
  // test), otherwise fall back to the instant local check computed from the
  // live input value. Surfaces the most common cause of lead-welcome
  // bounces — domain typos like gmail.con / gmal.com / .con TLDs.
  const emailSuggestion = useMemo(
    () => serverVerdict?.suggestion ?? suggestEmailCorrection(quoteData.email || ""),
    [serverVerdict, quoteData.email],
  );

  const acceptSuggestion = () => {
    if (!emailSuggestion) return;
    quoteData.setEmail(emailSuggestion);
    form.setFieldValue("email", emailSuggestion);
    latestEmailRef.current = emailSuggestion;
    setServerVerdict(null);
    form.validateFields(["email"]).catch(() => {});
  };

  // Layers the live endpoint verdict on top of the existing sync rules.
  // Scoped to onBlur (validateTrigger is a subset of the Form.Item's own,
  // widened below) so it fires once the user leaves the field or right
  // before Next — never per keystroke. A definitive negative (syntax /
  // junk_domain / no_mx) hard-blocks; anything else (typo_domain, an
  // ambiguous MX result, a network blip) passes, fail-open.
  const emailFormRules = useMemo<Rule[]>(
    () => [
      ...emailRules,
      {
        validateTrigger: "onBlur",
        validator: async (_, value: string) => {
          if (!value || !value.includes("@")) return Promise.resolve();
          const verdict = await checkEmail(value);
          // The field may have changed again since this check was kicked
          // off (blur, then edit, with no second blur to supersede the
          // in-flight request — see latestEmailRef's comment above). Drop a
          // now-stale verdict rather than applying it to a value that's no
          // longer on screen; the next blur will re-check the current one.
          if (latestEmailRef.current !== value) return Promise.resolve();
          setServerVerdict(verdict);
          if (verdict.status === "invalid") {
            return Promise.reject(new Error(EMAIL_UNDELIVERABLE_MESSAGE));
          }
          return Promise.resolve();
        },
      },
    ],
    [checkEmail],
  );

  // Phone format (2026-09-13): the field had NO rules at all, so a patient
  // could carry "call me" through to the lead. Same 10-digit / 11-with-leading-1
  // NANP lane the /live forms and the marketing site use. An EMPTY value passes
  // here on purpose — emptiness is the missing-field set's job (it keeps the
  // CTA disabled), and rejecting it here would paint a red error on a field the
  // patient hasn't reached yet.
  const phoneFormRules = useMemo<Rule[]>(
    () => [
      {
        validator: (_, value: string) => {
          if (!value) return Promise.resolve();
          if (isLooseUsPhone(value)) return Promise.resolve();
          return Promise.reject(new Error(t("contact.phoneInvalid")));
        },
      },
    ],
    [t],
  );

  useImperativeHandle(ref, () => ({
    focusField: (field: QuoteContactFormField) => {
      try {
        form.scrollToField(field, { behavior: "smooth", block: "center" });
      } catch {
        /* field not mounted yet — focus below still handles the common case */
      }
      const instance = form.getFieldInstance(field) as
        | { focus?: () => void }
        | undefined;
      instance?.focus?.();
    },
  }));

  return (
    <Form
      className="contact-form"
      layout="vertical"
      form={form}
      initialValues={{ layout: "vertical" }}
    >
      <Form.Item
        className="contact-form__item"
        name="name"
        rules={stringRules}
      >
        <StyledAntdInput
          value={quoteData.name}
          onChange={(e) => {
            quoteData.setName(e.target.value);
          }}
          placeholder={t("contact.firstName")}
          autoComplete="given-name"
        />
      </Form.Item>

      <Form.Item
        className="contact-form__item"
        name="lastname"
        rules={stringRules}
      >
        <StyledAntdInput
          value={quoteData.lastName}
          onChange={(e) => {
            quoteData.setLastName(e.target.value);
          }}
          placeholder={t("contact.lastName")}
          autoComplete="family-name"
        />
      </Form.Item>

      <Form.Item
        className="contact-form__item"
        name="email"
        rules={emailFormRules}
        validateTrigger={["onChange", "onBlur"]}
        extra={
          emailSuggestion ? (
            <span className="contact-form__email-suggestion">
              {t("contact.didYouMean", { defaultValue: "Did you mean" })}{" "}
              <button
                type="button"
                className="contact-form__email-suggestion-button"
                onClick={acceptSuggestion}
              >
                {emailSuggestion}
              </button>
              ?
            </span>
          ) : undefined
        }
      >
        <StyledAntdInput
          value={quoteData.email}
          type="email"
          onChange={(e) => {
            quoteData.setEmail(e.target.value);
            // Updated synchronously (ahead of the quoteData.email effect
            // above) so an in-flight blur-check that resolves mid-keystroke
            // sees the very latest value, not last render's.
            latestEmailRef.current = e.target.value;
            // Clear the last server verdict as soon as the user edits again
            // — rc-field-form re-runs the onChange-triggered rules (which
            // wipes the visible error either way) and drops it, but not the
            // onBlur-scoped async rule below, so this keeps the lifted
            // "blocked" state and the Did-you-mean hint in sync with what's
            // actually on screen until the next blur re-checks.
            setServerVerdict(null);
          }}
          placeholder={t("contact.email")}
          autoComplete="email"
          inputMode="email"
        />
      </Form.Item>

      <Form.Item
        className="contact-form__item"
        name="phone"
        rules={phoneFormRules}
      >
        <StyledAntdInput
          value={quoteData.phone}
          type="tel"
          onChange={(e) => {
            quoteData.setPhone(e.target.value);
          }}
          placeholder={t("contact.phone")}
          autoComplete="tel"
          inputMode="tel"
        />
      </Form.Item>

      <Form.Item
        className="contact-form__item"
        name="zipCode"
        rules={zipRules}
      >
        <StyledAntdInput
          value={quoteData.zipCode}
          onChange={(e) => {
            quoteData.setZipCode(e.target.value);
          }}
          placeholder={t("contact.zipCode")}
          autoComplete="postal-code"
          inputMode="numeric"
        />
      </Form.Item>
    </Form>
  );
});

QuotePersonalDetailsForm.displayName = "QuotePersonalDetailsForm";

export default QuotePersonalDetailsForm;
